<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import api from '@/api'
import { useI18n } from '@/i18n'
import { useUserStore } from '@/stores/user'
import { useNotify } from '@/services/useNotify'
import { useConfirmDelete } from '@/composables/useConfirmDelete'
import AppDialog from '@/components/ui/AppDialog.vue'

const props = defineProps({ groupId: { type: Number, required: true } })
const { t }    = useI18n()
const user     = useUserStore()
const notify   = useNotify()
const { confirmDelete } = useConfirmDelete()

const profiles  = ref([])
const loading   = ref(false)
const error     = ref('')
const showDialog = ref(false)
const newUrl     = ref('')
const saving     = ref(false)

const canEdit = computed(() => user.can('hackwatch_groups.change'))

const isDuplicate = computed(() => {
  const url = newUrl.value.trim().toLowerCase()
  if (!url) return false
  return profiles.value.some(p => p.fqdn.toLowerCase() === url)
})

const columns = [
  { field: 'fqdn',       header: t('hackwatch.groupDetail.profileColUrl'),   sortable: false },
  { field: 'created_at', header: t('hackwatch.groupDetail.profileColAdded'), sortable: true  },
  { field: '_del',       header: '',                                          sortable: false },
]

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.getHackerGroupProfiles(props.groupId)
    profiles.value = Array.isArray(data) ? data : []
  } catch {
    error.value = t('hackwatch.groupDetail.profilesError')
  } finally {
    loading.value = false
  }
}

function openDialog() {
  newUrl.value = ''
  showDialog.value = true
}

async function save() {
  const url = newUrl.value.trim()
  if (!url || isDuplicate.value) return
  saving.value = true
  try {
    await api.createHackerGroupProfile(props.groupId, { url })
    notify.success(t('hackwatch.groupDetail.profileAdded'))
    showDialog.value = false
    await load()
  } catch (e) {
    if (e?.response?.status === 409) {
      notify.error(t('hackwatch.groupDetail.profileDuplicate'))
    } else {
      notify.error(t('hackwatch.groupDetail.profileAddFailed'))
    }
  } finally {
    saving.value = false
  }
}

async function remove(profile) {
  if (!await confirmDelete(t('hackwatch.groupDetail.profileDeleteConfirm'))) return
  try {
    await api.deleteHackerGroupProfile(props.groupId, profile.profile_id)
    notify.success(t('hackwatch.groupDetail.profileDeleted'))
    await load()
  } catch {
    notify.error(t('hackwatch.groupDetail.profileDeleteFailed'))
  }
}

onMounted(load)
watch(() => props.groupId, load)
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-book hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendProfiles') }}</span>
      <pButton
        v-if="canEdit"
        icon="pi pi-plus"
        :label="t('hackwatch.groupDetail.profileAdd')"
        size="small"
        severity="secondary"
        outlined
        @click="openDialog"
      />
    </div>

    <AppTableWidget
      :columns="canEdit ? columns : columns.filter(c => c.field !== '_del')"
      :data="profiles"
      :loading="loading"
      :paginated="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      :searchable="true"
      data-key="profile_id"
      :empty-text="t('hackwatch.groupDetail.profilesEmpty')"
      @refresh="load"
    >
      <template #cell-fqdn="{ row }">
        <a :href="row.fqdn" target="_blank" rel="noopener" class="source-link">
          <i class="pi pi-external-link mr-1" />{{ row.fqdn }}
        </a>
      </template>
      <template #cell-created_at="{ row }">
        {{ new Date(row.created_at).toLocaleDateString() }}
      </template>
      <template #cell-_del="{ row }">
        <pButton
          icon="pi pi-trash"
          text
          rounded
          size="small"
          severity="danger"
          v-tooltip.top="t('common.delete')"
          @click="remove(row)"
        />
      </template>
    </AppTableWidget>

    <div v-if="error" class="mt-1 text-sm" style="color: var(--p-danger)">{{ error }}</div>

    <!-- Add dialog -->
    <AppDialog
      v-model="showDialog"
      :title="t('hackwatch.groupDetail.profileAddDialogTitle')"
      size="md"
      :show-footer="true"
      :loading="saving"
      @submit="save"
      @cancel="showDialog = false"
    >
      <div class="dialog-form">
        <div class="form-field required">
          <label>{{ t('hackwatch.groupDetail.profileUrlLabel') }}</label>
          <InputText
            v-model="newUrl"
            :placeholder="t('hackwatch.groupDetail.profileUrlPlaceholder')"
            :invalid="isDuplicate"
            class="w-full"
            autofocus
          />
          <small v-if="isDuplicate" class="duplicate-hint">
            <i class="pi pi-exclamation-triangle mr-1" />{{ t('hackwatch.groupDetail.profileDuplicate') }}
          </small>
        </div>
      </div>
      <template #footer>
        <pButton :label="t('common.cancel')" severity="secondary" text @click="showDialog = false" />
        <pButton
          :label="t('common.save')"
          :disabled="isDuplicate || !newUrl.trim()"
          :loading="saving"
          @click="save"
        />
      </template>
    </AppDialog>
  </div>
</template>

<style scoped>
.source-link {
  color: var(--p-primary-color);
  text-decoration: none;
  word-break: break-all;
  font-size: 0.85rem;
}
.source-link:hover {
  text-decoration: underline;
}
.duplicate-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  color: var(--p-danger-color, var(--p-red-500));
}
</style>
